import Uppy, { type Meta, type Body } from "@uppy/core";
import Dashboard from "@uppy/dashboard";
import Webcam from "@uppy/webcam";
import ImageEditor from "@uppy/image-editor";
import DropTarget from "@uppy/drop-target";
import XHRUpload from "@uppy/xhr-upload";

import "@uppy/core/css/style.css";
import "@uppy/dashboard/css/style.css";
import "@uppy/image-editor/css/style.css";
import Form from "@uppy/form";

export interface UppyUploaderConfig {
    targetDivId: string;
    formId: string;
    resultFieldId: string;
    dropTarget: string | HTMLElement;
    uploadUrlTemplate: string;
}

const formUppyControlsCount: Map<string, number> = new Map();
const formUppyControlsUploaded: Map<string, number> = new Map();

export function addUppyControl(config: UppyUploaderConfig): Uppy<Meta, Body> {

    const form = window.document.getElementById(config.formId);
    if (!form) {
        throw new Error("Form with id " + config.formId + " not found");
    }

    if (formUppyControlsCount.has(config.formId)) {
        const count = formUppyControlsCount.get(config.formId);
        formUppyControlsCount.set(config.formId, count + 1);
    } else {
        formUppyControlsCount.set(config.formId, 1);
    }

    form.addEventListener("submit", (event) => {
        formUppyControlsUploaded.set(config.formId, 0);
    });

    const uppy = new Uppy<Meta, Body>(
        {
            restrictions: {
                minNumberOfFiles: 1,
                maxNumberOfFiles: 1,
                allowedFileTypes: ["image/*"],
                maxFileSize: 10 * 1024 * 1024
            },
            onBeforeUpload: (files) => {
                if (Object.keys(files).length < 1) {
                    alert("Please select an image first!");
                    return false;
                }
                return true;
            }
        })
        .use(Dashboard, {
            inline: true,
            target: "#" + config.targetDivId,
            proudlyDisplayPoweredByUppy: false,
            theme: "dark",
            hideUploadButton: true,
            hideCancelButton: true,
            disableStatusBar: true,
            autoOpen: "imageEditor",
            width: "500px",
            height: "400px",
        });
    uppy.use(Webcam, {
            target: uppy.getPlugin("Dashboard"),
            showVideoSourceDropdown: false,
            showRecordingLength: true,
            modes: ["picture"]
        })
        .use(ImageEditor, {target: uppy.getPlugin("Dashboard")})
        .use(DropTarget, {
            target: config.dropTarget
        })
        .use(Form, {
            target: "#" + config.formId,
            getMetaFromForm: false,
            addResultToForm: true,
            resultName: config.resultFieldId,
            triggerUploadOnSubmit: true,
            submitOnSuccess: false
        });

    // Azure Blob uses a signed raw PUT, not the S3 signing protocol.
    uppy.use(XHRUpload, {
        endpoint: (file) => {
            if (Array.isArray(file)) throw new Error("Bundled Azure uploads are not supported");
            return config.uploadUrlTemplate.replaceAll("{filename}", encodeURIComponent(file.name));
        },
        method: "PUT",
        formData: false,
        headers: {"x-ms-blob-type": "BlockBlob"},
        // Azure returns an empty body. Preserve the URL consumed by the Django form.
        getResponseData: (xhr) => ({url: xhr.responseURL.split("?")[0]}),
    });

    uppy.on("complete", (result) => {
        if (result.failed.length === 0) {
            console.log("Upload successful");

            const expectedUploadCount = formUppyControlsCount.get(config.formId);
            let actualUploadCount = formUppyControlsUploaded.get(config.formId);
            actualUploadCount++;
            formUppyControlsUploaded.set(config.formId, actualUploadCount);
            if (expectedUploadCount === actualUploadCount) {
                const form = window.document.getElementById(config.formId) as HTMLFormElement;
                form.submit();
                console.log("Form submitted");
            } else {
                console.log("Waiting for other uploads. Expected: " + expectedUploadCount + " Actual: " + actualUploadCount);
            }

        } else {
            console.warn("Upload failed");
        }
    });

    return uppy;
}
